import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Save, Plus, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const HeroEditor = () => {
  const [hero, setHero] = useState({
    badge: '',
    badge_text: '',
    title: '',
    subtitle: '',
    cta_text: '',
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageType, setNewImageType] = useState('screenshot');
  const [newScreenshotUrl, setNewScreenshotUrl] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchHero();
  }, []);

  const fetchHero = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/hero`);
      setHero(response.data);
    } catch (error) {
      console.error('Failed to fetch hero:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/hero`, hero);
      toast({
        title: 'Success',
        description: 'Hero section updated successfully'
      });
      fetchHero();
    } catch (error) {
      console.error('Save failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to save hero section',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      const imageData = {
        type: newImageType,
        url: newImageUrl.trim()
      };
      
      if (newImageType === 'phone' && newScreenshotUrl.trim()) {
        imageData.screenshot = newScreenshotUrl.trim();
      }
      
      setHero({
        ...hero,
        images: [...hero.images, imageData]
      });
      setNewImageUrl('');
      setNewScreenshotUrl('');
      setNewImageType('screenshot');
    }
  };

  const removeImage = (index) => {
    setHero({
      ...hero,
      images: hero.images.filter((_, i) => i !== index)
    });
  };

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Hero Section</h2>
        <Button onClick={handleSave} disabled={loading} className="bg-pink-500 hover:bg-pink-600">
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Label htmlFor="badge">Badge Text</Label>
            <Input
              id="badge"
              value={hero.badge}
              onChange={(e) => setHero({ ...hero, badge: e.target.value })}
              placeholder="e.g., New"
            />
          </div>
          <div>
            <Label htmlFor="badge_text">Badge Description</Label>
            <Input
              id="badge_text"
              value={hero.badge_text}
              onChange={(e) => setHero({ ...hero, badge_text: e.target.value })}
              placeholder="e.g., Welcome to TokoSprint"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="title">Title</Label>
          <Textarea
            id="title"
            value={hero.title}
            onChange={(e) => setHero({ ...hero, title: e.target.value })}
            placeholder="Main hero title"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="subtitle">Subtitle</Label>
          <Textarea
            id="subtitle"
            value={hero.subtitle}
            onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
            placeholder="Hero subtitle"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="cta_text">CTA Button Text</Label>
          <Input
            id="cta_text"
            value={hero.cta_text}
            onChange={(e) => setHero({ ...hero, cta_text: e.target.value })}
            placeholder="e.g., Get Started"
          />
        </div>

        <div>
          <Label>Hero Images (Dynamic Content)</Label>
          <div className="space-y-3 mt-2">
            {hero.images.map((img, index) => {
              const imageData = typeof img === 'string' ? { type: 'screenshot', url: img } : img;
              return (
                <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-pink-500 uppercase">{imageData.type}</span>
                      {imageData.type === 'phone' && imageData.screenshot && (
                        <span className="text-xs text-gray-500">+ Screenshot</span>
                      )}
                    </div>
                    <Input value={imageData.url} readOnly className="text-sm" />
                    {imageData.screenshot && (
                      <Input value={imageData.screenshot} readOnly className="text-sm" placeholder="Screenshot inside phone" />
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => removeImage(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
              <div>
                <Label className="text-sm">Image Type</Label>
                <select
                  value={newImageType}
                  onChange={(e) => setNewImageType(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="screenshot">Screenshot / Dashboard</option>
                  <option value="person">Person / Team Photo</option>
                  <option value="phone">Phone Mockup (with screenshot)</option>
                </select>
              </div>
              
              <div>
                <Label className="text-sm">
                  {newImageType === 'phone' ? 'Phone Mockup URL' : 'Image URL'}
                </Label>
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Paste URL from Media Library"
                />
              </div>
              
              {newImageType === 'phone' && (
                <div>
                  <Label className="text-sm">Screenshot Inside Phone</Label>
                  <Input
                    value={newScreenshotUrl}
                    onChange={(e) => setNewScreenshotUrl(e.target.value)}
                    placeholder="Paste screenshot URL"
                  />
                </div>
              )}
              
              <Button onClick={addImage} className="w-full" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add to Hero
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default HeroEditor;
